import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  useAssignVoiceIdentity,
  useClearVoiceIdentityOverride
} from '@utils/api/editors/voice-identifications';
import { Speaker } from '@utils/types/organization/speaker.ts';
import { VoiceIdentification } from '@utils/types/organization/voice-profile.ts';
import { voiceErrorKey } from '@utils/editors/voice-profile-errors.ts';
import { AssignPersonDialog } from './assign-person-dialog.tsx';
import { SaveVoiceSampleDialog } from './save-voice-sample-dialog.tsx';
import { VoiceSuggestionChip } from './voice-suggestion-chip.tsx';
import { useSpeakerNames } from './use-speaker-names.ts';
import { VoiceProfileFeature } from './use-voice-profile-feature.ts';
import { VoiceIdentityState } from './use-voice-identity.ts';

export interface VoiceSpeakerRowOptions {
  sessionId: string;
  documentId: string;
  /** Row position in the source audio, in milliseconds. */
  startMs: number;
  stopMs?: number;
  /** The display name currently on the row. */
  currentName: string;
  feature: VoiceProfileFeature;
  identity: VoiceIdentityState;
  /** Apply an accepted name through the editor's normal edit/save path, so
   *  reopening and exports agree with what the user sees. */
  onApplyName?: (name: string) => void;
  /** Called the first time the user opens a voice control. The editor uses it
   *  to start fetching identity data it did not fetch up front. */
  onActivate?: () => void;
}

export interface VoiceSpeakerRow {
  /** The original diarization label for this row, if it resolves. */
  speakerLabel: string | null;
  identification: VoiceIdentification | undefined;
  /** True when any voice action is available on this row. */
  hasActions: boolean;
  /** Whether to offer the action — a question of role and surface only.
   *  Deliberately independent of whether the label has resolved yet: the
   *  timeline may not have been fetched, and a document may have no
   *  diarization at all. Both are explained inside the dialog. */
  canAssign: boolean;
  canEnroll: boolean;
  /** Set when this document can never support voice actions, so the editor
   *  can say why instead of silently offering nothing. */
  unavailableReason: 'no-diarization' | null;
  openAssign: () => void;
  openEnroll: () => void;
  /** Suggestion/decision chip to render beside the speaker label. */
  chip: ReactNode;
  /** Dialogs to render once per row. */
  dialogs: ReactNode;
}

/** Wire one editor row to voice identity: label, suggestion, dialogs.
 *
 * Both editors ask the same thing of this layer, so the transcript speaker
 * menu and the caption speakers panel share it rather than each growing their
 * own copy of the mutations and dialog state. */
export const useVoiceSpeakerRow = ({
  sessionId,
  documentId,
  startMs,
  stopMs,
  currentName,
  feature,
  identity,
  onApplyName,
  onActivate
}: VoiceSpeakerRowOptions): VoiceSpeakerRow => {
  const { t } = useTranslation();
  const [assignOpen, setAssignOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollAfterAssign, setEnrollAfterAssign] = useState(false);
  const [enrollPerson, setEnrollPerson] = useState<Speaker | null>(null);

  const assign = useAssignVoiceIdentity(sessionId, documentId);
  const clearOverride = useClearVoiceIdentityOverride(sessionId, documentId);

  const speakerLabel = useMemo(
    () => identity.labelAt(startMs, stopMs),
    [identity, startMs, stopMs]
  );

  const identification = speakerLabel
    ? identity.byLabel.get(speakerLabel)
    : undefined;

  // Only the ids this row actually references need resolving to names.
  const referencedIds = useMemo(() => {
    const ids: number[] = [];
    if (identification?.suggested_speaker_id != null)
      ids.push(identification.suggested_speaker_id);
    if (identification?.override_speaker_id != null)
      ids.push(identification.override_speaker_id);
    if (identification?.effective_speaker_id != null)
      ids.push(identification.effective_speaker_id);
    return ids;
  }, [identification]);

  const resolveSpeakerName = useSpeakerNames(
    referencedIds,
    feature.canAssign || feature.canReviewSuggestions
  );

  const labelGroup = useMemo(
    () => identity.labels.find(item => item.speaker_label === speakerLabel),
    [identity.labels, speakerLabel]
  );

  const handleAssign = async (speakerId: number | null, person?: Speaker) => {
    if (!speakerLabel) return;
    try {
      await assign.mutateAsync({ speakerLabel, speakerId });
      if (speakerId != null && person) {
        onApplyName?.(person.name);
        if (enrollAfterAssign) {
          setEnrollPerson(person);
          setEnrollOpen(true);
        }
      }
      setEnrollAfterAssign(false);
      toast.success(
        speakerId === null
          ? t('voice-marked-unknown')
          : t('voice-identity-saved')
      );
      setAssignOpen(false);
    } catch {
      // Keep the dialog open so the chosen person survives the failure. The
      // dialog renders the specific reason itself from `assign.error`, so a
      // toast here would only say the same thing twice.
    }
  };

  const handleClearOverride = async () => {
    if (!speakerLabel) return;
    try {
      await clearOverride.mutateAsync({ speakerLabel });
      toast.success(t('voice-decision-cleared'));
      setAssignOpen(false);
    } catch {
      // Shown inline in the dialog; see above.
    }
  };

  /** Accept the model's suggestion: record the human decision, then apply the
   *  name through the editor's own save path. The decision is recorded first —
   *  if it fails, the document is left untouched rather than showing a name
   *  that nothing backs. */
  const handleUseSuggestion = async (speakerId: number, name: string) => {
    if (!speakerLabel) return;
    try {
      await assign.mutateAsync({ speakerLabel, speakerId });
      onApplyName?.(name);
      toast.success(t('voice-identity-saved'));
    } catch (error) {
      toast.error(t(voiceErrorKey(error)));
    }
  };

  const openAssign = () => {
    onActivate?.();
    setEnrollAfterAssign(false);
    setAssignOpen(true);
  };

  const openEnroll = () => {
    onActivate?.();
    // Enrollment needs a directory person. The effective identity is the
    // natural default; without one the user picks a person first, which is
    // also the moment the identity decision gets recorded.
    const personId = identification?.effective_speaker_id ?? null;
    const personName =
      personId != null ? resolveSpeakerName(personId) : undefined;
    if (personId == null || !personName) {
      toast(t('voice-assign-person-before-enrolling'));
      setEnrollAfterAssign(true);
      setAssignOpen(true);
      return;
    }
    setEnrollPerson({ id: personId, name: personName });
    setEnrollOpen(true);
  };

  // Role and surface decide whether to offer the action. The label is resolved
  // when the dialog opens — it may need a fetch, and some documents have no
  // diarization at all — and the dialog explains either case.
  const canAssign = feature.canAssign;
  const canEnroll = feature.canEnroll && feature.sourceAvailable;

  const unavailableReason = identity.noDiarization ? 'no-diarization' : null;

  const chip =
    identification &&
    (identification.override_set || feature.canReviewSuggestions) &&
    speakerLabel ? (
      <VoiceSuggestionChip
        identification={identification}
        resolveSpeakerName={resolveSpeakerName}
        currentName={currentName}
        onUseSuggestion={(id, name) => void handleUseSuggestion(id, name)}
        onChoosePerson={openAssign}
        disabled={!canAssign || assign.isLoading}
      />
    ) : null;

  const dialogs = (
    <>
      {assignOpen && (
        <AssignPersonDialog
          isOpen
          onClose={() => {
            setAssignOpen(false);
            setEnrollAfterAssign(false);
          }}
          speakerLabel={speakerLabel}
          // The timeline may still be in flight, or the document may have no
          // diarization — the dialog says so instead of showing an empty list.
          isResolvingLabel={identity.isLoading}
          unavailableReason={unavailableReason}
          identification={identification}
          resolveSpeakerName={resolveSpeakerName}
          onAssign={handleAssign}
          onClearOverride={
            identification?.override_set ? handleClearOverride : undefined
          }
          isSaving={assign.isLoading || clearOverride.isLoading}
          error={assign.error ?? clearOverride.error}
        />
      )}

      {enrollOpen && speakerLabel && enrollPerson && (
        <SaveVoiceSampleDialog
          isOpen
          onClose={() => setEnrollOpen(false)}
          sessionId={sessionId}
          documentId={documentId}
          speakerLabel={speakerLabel}
          label={labelGroup}
          person={enrollPerson}
          recognitionAvailable={feature.canReviewSuggestions}
          minSampleSeconds={feature.minSampleSeconds}
          maxSampleSeconds={feature.maxSampleSeconds}
          maxSamplesPerSpeaker={feature.maxSamplesPerSpeaker}
        />
      )}
    </>
  );

  return {
    speakerLabel,
    identification,
    hasActions: canAssign || canEnroll,
    canAssign,
    canEnroll,
    unavailableReason,
    openAssign,
    openEnroll,
    chip,
    dialogs
  };
};
